import { defineMessages } from 'react-intl';

export const messages: {
	connect: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	connectLater: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	dismiss: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	bannerImageAlt: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	authorizationFail: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	authorizationSuccess: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	connect: {
		id: 'rovo.search.inline-3p.connect',
		defaultMessage: 'Connect {connectorName}',
		description: 'Button text to connect a third-party app',
	},
	connectLater: {
		id: 'rovo.search.inline-3p.connect-later',
		defaultMessage: 'Connect later',
		description: 'Button text to snooze the banner and connect later',
	},
	dismiss: {
		id: 'rovo.search.inline-3p.dismiss',
		defaultMessage: 'Dismiss',
		description: 'Button text to dismiss the banner',
	},
	bannerImageAlt: {
		id: 'rovo.search.inline-3p.banner-image-alt',
		defaultMessage: 'Illustration showing {connectorName} integration with Atlassian search',
		description: 'Alt text for the banner image showing third-party connector integration',
	},
	authorizationFail: {
		id: 'rovo.search.inline-authorization-fail',
		defaultMessage: 'Failed to connect to {product}.',
		description: 'Message displayed on flag when authorization fails.',
	},
	authorizationSuccess: {
		id: 'rovo.search.inline.authorization-success',
		defaultMessage: 'Account connected to {product}.',
		description: 'Message displayed on flag when authorization is successful.',
	},
});

// Full connector variant messages
export const fullConnectorMessages: {
	'admin-blessed': {
		heading: {
			id: string;
			defaultMessage: string;
			description: string;
		};
		body: {
			id: string;
			defaultMessage: string;
			description: string;
		};
	};
	fomo: {
		heading: {
			id: string;
			defaultMessage: string;
			description: string;
		};
		body: {
			id: string;
			defaultMessage: string;
			description: string;
		};
	};
	'search-in-one-place': {
		heading: {
			id: string;
			defaultMessage: string;
			description: string;
		};
		body: {
			id: string;
			defaultMessage: string;
			description: string;
		};
	};
} = {
	'admin-blessed': defineMessages({
		heading: {
			id: 'rovo.search.inline-3p.full-connector.admin-blessed.heading',
			defaultMessage: 'Your admin added {connectorName}',
			description: 'Heading for admin-blessed full connector variant',
		},
		body: {
			id: 'rovo.search.inline-3p.full-connector.admin-blessed.body',
			defaultMessage:
				'Finish connecting your account to unlock search results with context from all your files.',
			description: 'Body text for admin-blessed full connector variant',
		},
	}),
	fomo: defineMessages({
		heading: {
			id: 'rovo.search.inline-3p.full-connector.fomo.heading',
			defaultMessage: 'Your team is getting results you might be missing',
			description: 'Heading for fomo full connector variant',
		},
		body: {
			id: 'rovo.search.inline-3p.full-connector.fomo.body-v2',
			defaultMessage:
				"Join your team in a smarter search. Connect your account to find answers hidden in your team's work.",
			description: 'Body text for fomo full connector variant',
		},
	}),
	'search-in-one-place': defineMessages({
		heading: {
			id: 'rovo.search.inline-3p.full-connector.search-in-one-place.heading',
			defaultMessage: 'See richer search results in every search',
			description: 'Heading for search-in-one-place full connector variant',
		},
		body: {
			id: 'rovo.search.inline-3p.full-connector.search-in-one-place.body',
			defaultMessage:
				'Connect your account to see missing results, including files and docs you already have access to.',
			description: 'Body text for search-in-one-place full connector variant',
		},
	}),
};

// Smart link connector variant messages
export const smartLinkConnectorMessages: {
	'social-proof-low': {
		heading: {
			id: string;
			defaultMessage: string;
			description: string;
		};
		body: {
			id: string;
			defaultMessage: string;
			description: string;
		};
	};
	'social-proof-high': {
		heading: {
			id: string;
			defaultMessage: string;
			description: string;
		};
		body: {
			id: string;
			defaultMessage: string;
			description: string;
		};
	};
	fomo: {
		heading: {
			id: string;
			defaultMessage: string;
			description: string;
		};
		body: {
			id: string;
			defaultMessage: string;
			description: string;
		};
	};
	'recency-1': {
		heading: {
			id: string;
			defaultMessage: string;
			description: string;
		};
		body: {
			id: string;
			defaultMessage: string;
			description: string;
		};
	};
	'recency-2': {
		heading: {
			id: string;
			defaultMessage: string;
			description: string;
		};
		body: {
			id: string;
			defaultMessage: string;
			description: string;
		};
	};
} = {
	'social-proof-low': defineMessages({
		heading: {
			id: 'rovo.search.inline-3p.smart-link-connector.social-proof-low.heading',
			defaultMessage: "Find what's hidden in your team's shared links",
			description: 'Heading for social-proof-low smart link connector variant',
		},
		body: {
			id: 'rovo.search.inline-3p.smart-link-connector.social-proof-low.body',
			defaultMessage:
				'Connect your account to search across {connectorName} files shared by your team via Smart Links.',
			description: 'Body text for social-proof-low smart link connector variant',
		},
	}),
	'social-proof-high': defineMessages({
		heading: {
			id: 'rovo.search.inline-3p.smart-link-connector.social-proof-high.heading-v2',
			defaultMessage: "Find missing results from your team's content",
			description: 'Heading for social-proof-high smart link connector variant',
		},
		body: {
			id: 'rovo.search.inline-3p.smart-link-connector.social-proof-high.body',
			defaultMessage:
				'{teamUsagePercentage}% of your team works with shared content. Connect your account so work shared via Smart Links shows up in Search.',
			description: 'Body text for social-proof-high smart link connector variant',
		},
	}),
	fomo: defineMessages({
		heading: {
			id: 'rovo.search.inline-3p.smart-link-connector.fomo.heading',
			defaultMessage: 'Your team is getting results you might be missing',
			description: 'Heading for fomo smart link connector variant',
		},
		body: {
			id: 'rovo.search.inline-3p.smart-link-connector.fomo.body-v2',
			defaultMessage:
				"Most of your team's Smart Links don't appear in search. Connect {connectorName} to expand your search.",
			description: 'Body text for fomo smart link connector variant',
		},
	}),
	'recency-1': defineMessages({
		heading: {
			id: 'rovo.search.inline-3p.smart-link-connector.recency-1.heading',
			defaultMessage: 'Missing that recent {connectorName} result?',
			description: 'Heading for recency-1 smart link connector variant',
		},
		body: {
			id: 'rovo.search.inline-3p.smart-link-connector.recency-1.body-v2',
			defaultMessage: "Connect your account to find work you've seen and its related content.",
			description: 'Body text for recency-1 smart link connector variant',
		},
	}),
	'recency-2': defineMessages({
		heading: {
			id: 'rovo.search.inline-3p.smart-link-connector.recency-2.heading',
			defaultMessage: 'See richer results in every search',
			description: 'Heading for recency-2 smart link connector variant',
		},
		body: {
			id: 'rovo.search.inline-3p.smart-link-connector.recency-2.body',
			defaultMessage: "Connect your account to find {connectorName} links you've seen before.",
			description: 'Body text for recency-2 smart link connector variant',
		},
	}),
};

export const variantMessages: {
	'full-connector': {
		'admin-blessed': {
			heading: {
				id: string;
				defaultMessage: string;
				description: string;
			};
			body: {
				id: string;
				defaultMessage: string;
				description: string;
			};
		};
		fomo: {
			heading: {
				id: string;
				defaultMessage: string;
				description: string;
			};
			body: {
				id: string;
				defaultMessage: string;
				description: string;
			};
		};
		'search-in-one-place': {
			heading: {
				id: string;
				defaultMessage: string;
				description: string;
			};
			body: {
				id: string;
				defaultMessage: string;
				description: string;
			};
		};
	};
	smartlinks: {
		'social-proof-low': {
			heading: {
				id: string;
				defaultMessage: string;
				description: string;
			};
			body: {
				id: string;
				defaultMessage: string;
				description: string;
			};
		};
		'social-proof-high': {
			heading: {
				id: string;
				defaultMessage: string;
				description: string;
			};
			body: {
				id: string;
				defaultMessage: string;
				description: string;
			};
		};
		fomo: {
			heading: {
				id: string;
				defaultMessage: string;
				description: string;
			};
			body: {
				id: string;
				defaultMessage: string;
				description: string;
			};
		};
		'recency-1': {
			heading: {
				id: string;
				defaultMessage: string;
				description: string;
			};
			body: {
				id: string;
				defaultMessage: string;
				description: string;
			};
		};
		'recency-2': {
			heading: {
				id: string;
				defaultMessage: string;
				description: string;
			};
			body: {
				id: string;
				defaultMessage: string;
				description: string;
			};
		};
	};
} = {
	'full-connector': fullConnectorMessages,
	smartlinks: smartLinkConnectorMessages,
};
